import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, FlaskConical } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { TID } from "../../lib/testIds";
import Particles from "./Particles";
import ExperienceDemoModal from "../demo/ExperienceDemoModal";

const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Hero = () => {
    const ref = useRef(null);
    const [showDemoModal, setShowDemoModal] = useState(false);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const contentY = useTransform(scrollYProgress, [0, 1], [0, -80]);
    const contentOpacity = useTransform(scrollYProgress, [0.55, 0.95], [1, 0]);

    // ── Random triangle blink (both themes) ──────────────────────────
    useEffect(() => {
        const ids   = new Set();
        // Track which triangle is mid-blink so they never fire together
        const isBlink = [false, false];

        const after = (fn, ms) => {
            const id = setTimeout(() => { ids.delete(id); fn(); }, ms);
            ids.add(id);
        };

        const blink = (idx) => {
            const other = 1 - idx;

            // If the other triangle is currently blinking, wait and retry
            if (isBlink[other]) {
                after(() => blink(idx), 600 + Math.random() * 400);
                return;
            }

            const el = document.querySelectorAll(".hero-triangle")[idx];
            // Retry in 300 ms if element isn't in the DOM yet
            if (!el) { after(() => blink(idx), 300); return; }

            isBlink[idx] = true;
            el.classList.add("hero-triangle-blink");

            // Remove class after animation (0.55 s) + tiny buffer
            after(() => {
                el.classList.remove("hero-triangle-blink");
                isBlink[idx] = false;
                // Next blink: 6s – 16s from now (very infrequent, almost rare)
                after(() => blink(idx), 6000 + Math.random() * 10000);
            }, 580);
        };

        // Give Framer Motion entrance animation time to settle before first blink
        // Stagger so they never start simultaneously
        after(() => blink(0), 800  + Math.random() * 600);
        after(() => blink(1), 1800 + Math.random() * 700);

        return () => ids.forEach(clearTimeout);
    }, []);

    const container = {
        hidden: {},
        show: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } },
    };
    const item = {
        hidden: { opacity: 0, y: 24, filter: "blur(8px)" },
        show: {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            transition: { duration: 1, ease: [0.16, 1, 0.3, 1] },
        },
    };

    // Letter-by-letter wordmark — both A's are glowing triangles
    const wordmark = [
        { c: "L" },
        { c: "\u25b3", triangle: true },
        { c: "M" },
        { c: "B" },
        { c: "D" },
        { c: "\u25b3", triangle: true },
        { c: " " },
        { c: "Q" },
        { c: "U" },
        { c: "E" },
        { c: "S" },
        { c: "T" },
    ];

    const letterVariant = {
        hidden: { opacity: 0, y: 70, filter: "blur(18px)" },
        show: (i) => ({
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            transition: {
                duration: 1.2,
                delay: 0.15 + i * 0.06,
                ease: [0.16, 1, 0.3, 1],
            },
        }),
    };

    return (
        <section
            ref={ref}
            id="hero"
            className="relative min-h-[100svh] flex flex-col items-center justify-center px-6 pt-28 pb-16 text-center overflow-hidden"
        >
            <Particles count={38} />

            {/* Ambient bloom - CSS class handles light/dark */}
            <div
                aria-hidden
                className="hero-bloom hero-bloom-bg pointer-events-none absolute left-1/2 top-[46%] -translate-x-1/2 -translate-y-1/2"
                style={{
                    width: "78vw",
                    maxWidth: 1200,
                    height: "44vh",
                    filter: "blur(40px)",
                }}
            />

            <motion.div
                style={{
                    y: contentY,
                    opacity: contentOpacity,
                }}
                variants={container}
                initial="hidden"
                animate="show"
                className="relative z-10 flex flex-col items-center w-full"
            >
                {/* Overline */}
                <motion.p
                    variants={item}
                    className="font-mono text-[10px] sm:text-xs tracking-[0.5em] uppercase text-[#9CA3AF] dark:text-white/45"
                >
                    Software Infrastructure · Education OS
                </motion.p>

                {/* MASSIVE wordmark */}
                <h1
                    data-testid={TID.heroWordmark}
                    className="hero-wordmark mt-10 select-none leading-none text-center"
                    style={{
                        fontSize: "clamp(48px, 11.5vw, 200px)",
                        maxWidth: "96vw",
                    }}
                >
                    <span
                        style={{
                            whiteSpace: "nowrap",
                            display: "inline-block",
                        }}
                    >
                        {wordmark.slice(0, 6).map((w, i) => (
                            <motion.span
                                key={i}
                                custom={i}
                                variants={letterVariant}
                                className={
                                    w.triangle ? "hero-triangle" : "hero-letter"
                                }
                                style={{
                                    animationDelay: `${i * 0.35}s`,
                                }}
                            >
                                {w.c}
                            </motion.span>
                        ))}
                    </span>
                    <span
                        style={{ display: "inline-block", width: "0.55em" }}
                    />
                    <span
                        style={{
                            whiteSpace: "nowrap",
                            display: "inline-block",
                        }}
                    >
                        {wordmark.slice(7).map((w, i) => (
                            <motion.span
                                key={`q${i}`}
                                custom={i + 7}
                                variants={letterVariant}
                                className="hero-letter"
                                style={{
                                    animationDelay: `${(i + 7) * 0.35}s`,
                                }}
                            >
                                {w.c}
                            </motion.span>
                        ))}
                    </span>
                </h1>

                {/* Divider beam */}
                <motion.div
                    variants={item}
                    className="mt-12 h-px w-32 bg-gradient-to-r from-transparent via-violet-400/40 to-transparent dark:via-white/40"
                />

                {/* Supporting headline */}
                <motion.p
                    variants={item}
                    data-testid={TID.heroHeadline}
                    className="mt-10 max-w-2xl font-display text-lg sm:text-xl md:text-2xl leading-snug text-[#374151] dark:text-white/80"
                >
                    Building world-class software infrastructure
                    <br className="hidden sm:block" /> for modern education and
                    training.
                </motion.p>

                <motion.p
                    variants={item}
                    className="mt-5 max-w-xl text-sm sm:text-base text-[#9CA3AF] dark:text-white/45 leading-relaxed"
                >
                    One unified platform for learning, assessments, analytics,
                    operations, and student success.
                </motion.p>

                {/* CTAs */}
                <motion.div
                    variants={item}
                    className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
                >
                    <button
                        onClick={() => scrollTo("waitlist")}
                        data-testid={TID.heroJoinBtn}
                        className="btn-glow group inline-flex items-center gap-2 rounded-full font-semibold px-8 py-4 transition-all duration-300 hover:scale-[1.03] hover:-translate-y-0.5 active:scale-[0.98]
                        bg-gradient-to-r from-violet-500 via-purple-500 to-cyan-400 text-white shadow-[0_4px_24px_rgba(139,92,246,0.4)]
                        dark:[background-image:none] dark:bg-white dark:text-black dark:shadow-none"
                    >
                        Join Waitlist
                        <ArrowRight
                            size={18}
                            className="transition-transform group-hover:translate-x-1"
                        />
                    </button>

                    <button
                        onClick={() => setShowDemoModal(true)}
                        data-testid="experience-demo-btn"
                        className="group inline-flex items-center gap-2 rounded-full font-semibold px-8 py-4 transition-all duration-300 hover:scale-[1.03] hover:-translate-y-0.5 active:scale-[0.98]
                        border border-violet-400/30 text-[#374151] hover:border-violet-400/60 hover:text-violet-600
                        dark:border-white/15 dark:text-white/70 dark:hover:border-white/35 dark:hover:text-white"
                        style={{ backdropFilter: 'blur(8px)', background: 'rgba(139,92,246,0.04)' }}
                    >
                        <FlaskConical size={16} className="opacity-70 group-hover:opacity-100 transition-opacity" />
                        Experience Demo
                    </button>

                    <button
                        onClick={() => scrollTo("vision")}
                        data-testid={TID.heroVisionBtn}
                        className="group inline-flex items-center gap-2 text-[#6B7280] hover:text-[#111827] dark:text-white/70 dark:hover:text-white font-medium px-3 py-3 transition-colors"
                    >
                        <span className="flex items-center justify-center w-7 h-7 rounded-full border border-[rgba(0,0,0,0.1)] group-hover:border-violet-400 group-hover:bg-violet-50 dark:border-white/15 dark:group-hover:border-brand-purple/60 dark:group-hover:bg-brand-purple/10 transition-colors">
                            <Play size={11} fill="currentColor" />
                        </span>
                        Watch Vision
                    </button>
                </motion.div>
            </motion.div>

            {/* Experience Demo Modal */}
            <ExperienceDemoModal
                open={showDemoModal}
                onClose={() => setShowDemoModal(false)}
            />

            {/* Scroll cue */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.2, duration: 0.8 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10"
            >
                <motion.div
                    animate={{ y: [0, 6, 0] }}
                    transition={{
                        duration: 2.2,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                    className="flex flex-col items-center gap-2 text-[#9CA3AF] dark:text-white/40"
                >
                    <span className="font-mono text-[10px] tracking-[0.4em] uppercase">
                        Scroll
                    </span>
                    <span className="h-8 w-px bg-gradient-to-b from-violet-400/60 to-transparent dark:from-white/60" />
                </motion.div>
            </motion.div>
        </section>
    );
};

export default Hero;
